import type { Product, RobuxProductConfig } from '#bv/data/products'
import { ROBUX_PRODUCT_ID } from '#bv/data/products'

export { ROBUX_PRODUCT_ID }

/** Roblox marketplace fee on gamepass sales (30%). */
export const ROBUX_PLATFORM_TAX_RATE = 0.3

/** Seller receives this share of the gamepass price (70%). */
export const ROBUX_CREATOR_SHARE = 1 - ROBUX_PLATFORM_TAX_RATE

export const ROBUX_MAX_AMOUNT = 10_000

export const ROBUX_MIN_AMOUNT = 1

export type RobuxQuote = {
  /** Robux the buyer wants to receive after platform tax. */
  desiredRobux: number
  /** Price to set on the gamepass listing (whole Robux). */
  gamepassPrice: number
  /** Platform fee rate used (e.g. 0.3). */
  platformTaxRate: number
  /** Robux kept by Roblox (tax on gamepass price). */
  platformTaxRobux: number
  /** Robux actually credited after tax (≥ desired, from gamepass price). */
  robuxAfterTax: number
  usdPrice: number
}

function creatorShareFromConfig(cfg: RobuxProductConfig): number {
  if (cfg.platformTaxRate != null && Number.isFinite(cfg.platformTaxRate)) {
    return 1 - cfg.platformTaxRate
  }
  if (cfg.creatorShare != null && Number.isFinite(cfg.creatorShare)) {
    return cfg.creatorShare
  }
  return ROBUX_CREATOR_SHARE
}

export function isRobuxProduct(p: Product | null | undefined): p is Product & { robuxConfig: RobuxProductConfig } {
  return Boolean(p?.robuxConfig && p.id === ROBUX_PRODUCT_ID)
}

/** Shop / PDP “From” price — $5 per 1,000 Robux list rate (not min-quantity calculator). */
export function robuxStorefrontFromPriceUsd(product: Product & { robuxConfig: RobuxProductConfig }): number {
  if (product.price > 0) return product.price
  return Math.round(product.robuxConfig.pricePerRobuxUsd * 1000 * 100) / 100
}

export function clampRobuxAmount(raw: number, cfg: RobuxProductConfig): number {
  const n = Math.floor(raw)
  if (!Number.isFinite(n)) return cfg.minRobux
  return Math.min(cfg.maxRobux, Math.max(cfg.minRobux, n))
}

/** Gamepass price so the buyer receives at least `desiredRobux` after Roblox's cut. */
export function gamepassPriceForRobux(desiredRobux: number, creatorShare = ROBUX_CREATOR_SHARE): number {
  const desired = Math.max(0, Math.floor(desiredRobux))
  if (desired <= 0) return 0
  return Math.ceil(desired / creatorShare)
}

export function robuxReceivedAfterTax(gamepassPrice: number, creatorShare = ROBUX_CREATOR_SHARE): number {
  const gp = Math.max(0, Math.floor(gamepassPrice))
  return Math.floor(gp * creatorShare)
}

export function usdPriceForRobux(desiredRobux: number, pricePerRobuxUsd: number): number {
  const robux = Math.max(0, Math.floor(desiredRobux))
  return Math.round(robux * pricePerRobuxUsd * 100) / 100
}

function pricePerRobuxUsdFromProductPrice(productPriceUsd?: number): number | null {
  if (productPriceUsd == null) return null
  const p = Number(productPriceUsd)
  if (!Number.isFinite(p) || p <= 0) return null
  // `product.price` is a storefront "from" price per 1,000 Robux.
  return p / 1000
}

/**
 * Quote a Robux purchase. Uses the live product price (admin dashboard) when provided,
 * so price edits propagate everywhere without needing to regenerate configs.
 */
export function quoteRobuxPurchase(
  desiredRobux: number,
  cfg: RobuxProductConfig,
  productPriceUsd?: number,
): RobuxQuote {
  const amount = clampRobuxAmount(desiredRobux, cfg)
  const share = creatorShareFromConfig(cfg)
  const platformTaxRate = 1 - share
  const gamepassPrice = gamepassPriceForRobux(amount, share)
  const robuxAfterTax = robuxReceivedAfterTax(gamepassPrice, share)
  const platformTaxRobux = gamepassPrice - robuxAfterTax
  const pricePerRobuxUsd = pricePerRobuxUsdFromProductPrice(productPriceUsd) ?? cfg.pricePerRobuxUsd
  const usdPrice = usdPriceForRobux(amount, pricePerRobuxUsd)
  return { desiredRobux: amount, gamepassPrice, platformTaxRate, platformTaxRobux, robuxAfterTax, usdPrice }
}

export function robuxCartLineName(quote: RobuxQuote): string {
  return `Robux — ${quote.desiredRobux.toLocaleString()} (gamepass ${quote.gamepassPrice.toLocaleString()} R$)`
}

const ROBLOX_GAMEPASS_PATH = /\/game-pass\//i

/** Normalize buyer gamepass URL for storage and fulfillment. */
export function normalizeRobuxGamepassLink(raw: string): string | null {
  const trimmed = raw.trim()
  if (!trimmed) return null
  try {
    const withProto = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
    const url = new URL(withProto)
    const host = url.hostname.toLowerCase()
    if (host !== 'www.roblox.com' && host !== 'roblox.com') return null
    if (!ROBLOX_GAMEPASS_PATH.test(url.pathname)) return null
    url.search = ''
    url.hash = ''
    return url.toString()
  } catch {
    return null
  }
}

export function validateRobuxGamepassLinkMessage(raw: string | undefined): string | null {
  if (!raw?.trim()) return 'Paste the link to your Roblox gamepass.'
  if (!normalizeRobuxGamepassLink(raw)) {
    return 'Enter a valid Roblox gamepass URL (https://www.roblox.com/game-pass/…).'
  }
  return null
}

export function robuxMetadataForCheckout(quote: RobuxQuote, gamepassLink: string): Record<string, string> {
  const link = normalizeRobuxGamepassLink(gamepassLink)
  if (!link) throw new Error('Invalid Robux gamepass link.')
  return {
    robux: '1',
    robuxAmount: String(quote.desiredRobux),
    robuxGamepassPrice: String(quote.gamepassPrice),
    robuxAfterTax: String(quote.robuxAfterTax),
    robuxPlatformTaxRate: String(quote.platformTaxRate),
    robuxPlatformTaxRobux: String(quote.platformTaxRobux),
    robuxGamepassLink: link,
  }
}

export function validateRobuxCartLine(
  line: {
    productId: number
    unitPrice: number
    quantity: number
    robuxAmount?: number
    robuxGamepassPrice?: number
    robuxGamepassLink?: string
  },
  product: Product | null,
): string | null {
  if (!isRobuxProduct(product)) return 'Robux product is not available.'
  if (line.quantity !== 1) return 'Robux orders must be quantity 1.'
  const amount = line.robuxAmount
  const gp = line.robuxGamepassPrice
  const linkErr = validateRobuxGamepassLinkMessage(line.robuxGamepassLink)
  if (linkErr) return linkErr
  if (amount == null || gp == null) return 'Enter a Robux amount on the product page.'
  const quote = quoteRobuxPurchase(amount, product.robuxConfig, product.price)
  if (quote.desiredRobux !== Math.floor(amount)) {
    return `Robux amount must be between ${product.robuxConfig.minRobux.toLocaleString()} and ${product.robuxConfig.maxRobux.toLocaleString()}.`
  }
  if (quote.gamepassPrice !== Math.floor(gp)) {
    return 'Gamepass price mismatch. Refresh the Robux page and try again.'
  }
  if (Math.abs(quote.usdPrice - line.unitPrice) > 0.02) {
    return 'Robux price mismatch. Refresh the product page and try again.'
  }
  return null
}
